import React, { useState, useEffect, useRef } from 'react';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import axiosPost from '../../../helpers/axios-helper';
import useCarouselReorder from './useCarouselReorder';
import axios from 'axios';
import config from '../../../constants/config';
import RichTextEditor from '../../../components/editor/RichTextEditor';
import FeatureEditor from '../components/FeatureEditor';
import slugify from '../slugify';
import ImageCropper from '../../../components/ImageCropper';
import VenueButtonsEditor from '../components/VenueButtonsEditor';

export default function Beaches() {
  const BEACH_TYPES = ['Sandy', 'Rocky', 'Nude', 'Beach Club', 'Cove', 'Lagoon'];

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({ country_id: '', active: '' });
  const reorder = useCarouselReorder({
    module: 'beaches',
    items: items,
    setItems: setItems,
    countryId: filters.country_id,
  });
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [cropperFile, setCropperFile] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  const [formData, setFormData] = useState({
    title: '', country_id: '', location: '', image: '', images: '', active: 1,
    queer_owned: 0, lgbtq_friendly: 0, beach_type: '', amenities: '',
    contact: { address: '', phone: '', email: '', website: '' }, content: ''
  });

  const [extras, setExtras] = useState([]);
  const [extraForm, setExtraForm] = useState({ id: 0, section_key: '', title: '', description: '', sort_order: 1, active: 1 });
  const extrasFormRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);

  const parseImagesString = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(s => (s || '').toString().trim()).filter(Boolean);
    return String(val).split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
  };

  const loadItems = (controller, overrides = {}) => {
    const payload = { page: 1, per_page: 50 };
    const cid = overrides.country_id ?? filters.country_id;
    const act = overrides.active ?? filters.active;
    if (cid) payload.country_id = parseInt(cid);
    if (act !== '' && act !== undefined && act !== null) payload.active = parseInt(act);
    axiosPost('beaches/list', { 'Content-Type': 'application/json' }, payload, (resp) => {
      setItems(resp?.success && resp?.data?.items ? resp.data.items : []);
      setIsLoading(false);
    }, controller);
  };

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    loadItems(controller);
    axiosPost('dashboard/countries/list', {}, {}, (resp) => {
      setCountries(Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []));
    }, controller);
    return () => controller.abort();
  }, []);

  const fetchExtras = (itemId) => {
    if (!itemId) { setExtras([]); return; }
    axiosPost('beaches/extras/list', { 'Content-Type': 'application/json' }, { beach_id: itemId }, (resp) => {
      setExtras(Array.isArray(resp?.data?.items) ? resp.data.items : []);
    });
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      ...item, title: item.title || '', country_id: item.country_id ?? '',
      location: item.location ?? '', image: item.image ?? '', images: item.images ?? '',
      active: item.active ?? 1, queer_owned: item.queer_owned ?? 0,
      lgbtq_friendly: item.lgbtq_friendly ?? 0, beach_type: item.beach_type ?? '',
      amenities: item.amenities || '',
      instagram_url: item.instagram_url ?? '', facebook_url: item.facebook_url ?? '', tiktok_url: item.tiktok_url ?? '',
      contact: item.contact || { address: '', phone: '', email: '', website: '' },
      content: item.content ?? ''
    });
    setIsEditing(true);
    fetchExtras(item.id);
    axiosPost('beaches/get', { 'Content-Type': 'application/json' }, { id: item.id }, (resp) => {
      const b = resp?.data?.beach;
      if (b) {
        setFormData(prev => ({ ...prev, amenities: b.amenities || '', images: b.images || '', instagram_url: b.instagram_url || '', facebook_url: b.facebook_url || '', tiktok_url: b.tiktok_url || '' }));
      }
    });
  };

  const handleAddNew = () => {
    setSelectedItem(null);
    setFormData({
      title: '', country_id: '', location: '', image: '', images: '', active: 1,
      queer_owned: 0, lgbtq_friendly: 0, beach_type: '', amenities: '',
      instagram_url: '', facebook_url: '', tiktok_url: '',
      contact: { address: '', phone: '', email: '', website: '' }, content: ''
    });
    setIsEditing(true); setExtras([]);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [p1, p2] = name.split('.');
      setFormData(prev => ({ ...prev, [p1]: { ...prev[p1], [p2]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      id: selectedItem?.id || 0, title: formData.title,
      country_id: parseInt(formData.country_id || 0),
      location: formData.location || '',
      address: formData.contact?.address || '', phone: formData.contact?.phone || '',
      website: formData.contact?.website || '', email: formData.contact?.email || '',
      description: formData.content || '', image: formData.image || '',
      images: parseImagesString(formData.images).join('\n'),
      amenities: formData.amenities || '',
      beach_type: formData.beach_type || '',
      lgbtq_friendly: formData.lgbtq_friendly ? 1 : 0,
      queer_owned: formData.queer_owned ? 1 : 0,
      instagram_url: formData.instagram_url || '',
      facebook_url: formData.facebook_url || '',
      tiktok_url: formData.tiktok_url || '',
      active: formData.active ? 1 : 0
    };
    axiosPost('beaches/update', { 'Content-Type': 'application/json' }, payload, (resp) => {
      if (resp?.success) { setIsLoading(true); loadItems(); setIsEditing(false); }
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this beach?')) {
      axiosPost('beaches/delete', { 'Content-Type': 'application/json' }, { id }, (resp) => {
        if (resp?.success) { setIsLoading(true); loadItems(); }
      });
    }
  };

  const handleImagesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingFiles(files.slice(1));
    setCropperFile(files[0]);
    try { e.target.value = ''; } catch (ex) {}
  };

  const uploadCroppedImage = async (file) => {
    const fd = new FormData(); fd.append('image', file);
    const resp = await axios.post(`${config.api.url}beaches/upload-image`, fd, {
      headers: { 'api': config.api.key, 'Content-Type': 'multipart/form-data' }
    });
    const url = resp?.data?.data?.url || resp?.data?.url;
    if (resp?.data?.success && url) {
      setFormData(prev => ({ ...prev, images: [...parseImagesString(prev.images), url].join('\n') }));
    }
  };

  const handleCropComplete = async (croppedFile) => {
    setCropperFile(null);
    setIsUploadingImages(true);
    try {
      await uploadCroppedImage(croppedFile);
      if (pendingFiles.length > 0) {
        const [nextFile, ...remaining] = pendingFiles;
        setPendingFiles(remaining);
        setCropperFile(nextFile);
      }
    } catch (err) { console.error('Image upload failed', err); }
    finally { if (pendingFiles.length === 0) setIsUploadingImages(false); }
  };

  const handleCropCancel = () => {
    setCropperFile(null);
    if (pendingFiles.length > 0) {
      const [nextFile, ...remaining] = pendingFiles;
      setPendingFiles(remaining);
      setCropperFile(nextFile);
    } else { setIsUploadingImages(false); }
  };

  const handleSkipCrop = async (file) => {
    setCropperFile(null);
    setIsUploadingImages(true);
    try {
      await uploadCroppedImage(file);
      if (pendingFiles.length > 0) {
        const [nextFile, ...remaining] = pendingFiles;
        setPendingFiles(remaining);
        setCropperFile(nextFile);
      }
    } catch (err) { console.error('Image upload failed', err); }
    finally { if (pendingFiles.length === 0) setIsUploadingImages(false); }
  };

  const removeImageAt = (idx) => {
    const arr = parseImagesString(formData.images); arr.splice(idx, 1);
    setFormData(prev => ({ ...prev, images: arr.join('\n') }));
  };

  const moveImage = (idx, dir) => {
    const arr = parseImagesString(formData.images);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    const temp = arr[idx]; arr[idx] = arr[newIdx]; arr[newIdx] = temp;
    setFormData(prev => ({ ...prev, images: arr.join('\n') }));
  };

  // Extras handlers
  const handleExtraChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExtraForm(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };
  const resetExtraForm = () => setExtraForm({ id: 0, section_key: '', title: '', description: '', sort_order: 1, active: 1 });
  const handleExtraEdit = (extra) => {
    setExtraForm({ id: extra.id, section_key: extra.section_key, title: extra.title || '', description: extra.description || '', sort_order: extra.sort_order || 1, active: extra.active ?? 1 });
    if (extrasFormRef.current) try { extrasFormRef.current.scrollIntoView({ behavior: 'smooth' }); } catch (e) {}
  };
  const handleExtraSubmit = (e) => {
    if (e) e.preventDefault();
    if (!selectedItem?.id) return;
    axiosPost('beaches/extras/upsert', { 'Content-Type': 'application/json' },
      { ...extraForm, beach_id: selectedItem.id, sort_order: parseInt(extraForm.sort_order || 1) },
      (resp) => { if (resp?.success) { resetExtraForm(); fetchExtras(selectedItem.id); } }
    );
  };
  const handleExtraToggle = (extra) => {
    axiosPost('beaches/extras/toggle', { 'Content-Type': 'application/json' },
      { id: extra.id, active: extra.active === 1 ? 0 : 1 },
      (resp) => { if (resp?.success) fetchExtras(selectedItem.id); }
    );
  };
  const onExtraDragStart = (e, index) => { setDragIndex(index); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(index)); };
  const onExtraDragOver = (e) => { e.preventDefault(); };
  const onExtraDrop = (e, index) => {
    e.preventDefault();
    const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(from) || from === index) { setDragIndex(null); return; }
    const newOrder = Array.from(extras); const [moved] = newOrder.splice(from, 1); newOrder.splice(index, 0, moved);
    setExtras(newOrder); setDragIndex(null);
    axiosPost('beaches/extras/reorder', { 'Content-Type': 'application/json' },
      { beach_id: selectedItem.id, items: newOrder.map(ex => ex.id) },
      (resp) => { if (resp?.success) fetchExtras(selectedItem.id); }
    );
  };

  const handleFilterChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const applyFilters = () => { setIsLoading(true); loadItems(); };
  const resetFilters = () => { const c = { country_id: '', active: '' }; setFilters(c); setIsLoading(true); loadItems(undefined, c); };

  return (
    <div>
      <div className="DashboardPanel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 className="DashboardTitle">Beaches Management</h1>
          <button className="DashboardButton" onClick={handleAddNew}><FaPlus style={{ marginRight: '5px' }} /> Add New Beach</button>
        </div>

        {isLoading ? (<p>Loading beaches...</p>) : isEditing ? (
          <form onSubmit={handleSubmit} className="DashboardForm">
            <h2 className="DashboardSubtitle">{selectedItem ? 'Edit Beach' : 'Add New Beach'}</h2>

            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Beach Name</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className="DashboardFormInput" required />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Country</label>
                <select name="country_id" value={formData.country_id} onChange={handleChange} className="DashboardFormInput" required>
                  <option value="">Select a country</option>
                  {countries.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Beach Type</label>
                <select name="beach_type" value={formData.beach_type} onChange={handleChange} className="DashboardFormInput">
                  <option value="">Select type</option>
                  {BEACH_TYPES.map(t => (<option key={t} value={t.toLowerCase()}>{t}</option>))}
                </select>
              </div>
            </div>

            <h3>Contact Information</h3>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Address</label>
              <input type="text" name="contact.address" value={formData.contact?.address || ''} onChange={handleChange} className="DashboardFormInput" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Phone</label>
                <input type="text" name="contact.phone" value={formData.contact?.phone || ''} onChange={handleChange} className="DashboardFormInput" />
              </div>
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Email</label>
                <input type="email" name="contact.email" value={formData.contact?.email || ''} onChange={handleChange} className="DashboardFormInput" />
              </div>
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Website</label>
              <input type="url" name="contact.website" value={formData.contact?.website || ''} onChange={handleChange} className="DashboardFormInput" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
              <div className="DashboardFormGroup" style={{ display: 'flex', alignItems: 'center' }}>
                <label className="DashboardFormLabel" style={{ marginRight: 10 }}>Active</label>
                <input type="checkbox" name="active" checked={!!formData.active} onChange={handleChange} style={{ transform: 'scale(1.2)' }} />
              </div>
              <div className="DashboardFormGroup" style={{ display: 'flex', alignItems: 'center' }}>
                <label className="DashboardFormLabel" style={{ marginRight: 10 }}>LGBTQ+ Friendly</label>
                <input type="checkbox" name="lgbtq_friendly" checked={!!formData.lgbtq_friendly} onChange={handleChange} style={{ transform: 'scale(1.2)' }} />
              </div>
              <div className="DashboardFormGroup" style={{ display: 'flex', alignItems: 'center' }}>
                <label className="DashboardFormLabel" style={{ marginRight: 10 }}>Queer Owned</label>
                <input type="checkbox" name="queer_owned" checked={!!formData.queer_owned} onChange={handleChange} style={{ transform: 'scale(1.2)' }} />
              </div>
            </div>

            <h3>Social Media</h3>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Instagram URL</label>
              <input type="url" name="instagram_url" value={formData.instagram_url || ''} onChange={handleChange} className="DashboardFormInput" placeholder="https://instagram.com/username" />
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Facebook URL</label>
              <input type="url" name="facebook_url" value={formData.facebook_url || ''} onChange={handleChange} className="DashboardFormInput" placeholder="https://facebook.com/pagename" />
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">TikTok URL</label>
              <input type="url" name="tiktok_url" value={formData.tiktok_url || ''} onChange={handleChange} className="DashboardFormInput" placeholder="https://tiktok.com/@username" />
            </div>

            <h3>Images</h3>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Hero Image URL</label>
              <input type="url" name="image" value={formData.image} onChange={handleChange} className="DashboardFormInput" />
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Upload Images</label>
              <input type="file" accept="image/*" multiple onChange={handleImagesSelected} disabled={isUploadingImages} />
              {isUploadingImages && <div style={{ marginTop: 6, fontSize: 12, color: '#555' }}>Uploading...</div>}
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Image URLs (newline or comma separated)</label>
              <textarea name="images" value={formData.images} onChange={handleChange} className="DashboardFormInput" rows="3" />
            </div>
            {parseImagesString(formData.images).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <label className="DashboardFormLabel" style={{ display: 'block', marginBottom: 6 }}>Preview & Order</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                  {parseImagesString(formData.images).map((url, idx) => (
                    <div key={url + idx} style={{ border: '1px solid #eee', borderRadius: 6, padding: 8 }}>
                      <div style={{ width: '100%', height: 90, background: `#f6f6f6 url(${url}) center/cover no-repeat`, borderRadius: 4 }} />
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        <button type="button" className="DashboardButtonSecondary" onClick={() => moveImage(idx, -1)} disabled={idx === 0}>Up</button>
                        <button type="button" className="DashboardButtonSecondary" onClick={() => moveImage(idx, 1)} disabled={idx === parseImagesString(formData.images).length - 1}>Down</button>
                        <button type="button" className="DashboardButtonSecondary" onClick={() => removeImageAt(idx)}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h3>Content</h3>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Description</label>
              <RichTextEditor
                content={formData.content}
                onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
                placeholder="Enter beach description..."
                minHeight={180}
              />
            </div>

            <VenueButtonsEditor itemType='beach' itemId={selectedItem?.id || 0} />

            <FeatureEditor
              itemType='beach'
              itemId={selectedItem?.id || 0}
              itemTitle={formData.title}
              publicPath={selectedItem?.id ? `/beach/${slugify(formData.title)}` : ''}
            />

            <h3>Extras</h3>
            {selectedItem?.id ? (
              <div style={{ marginBottom: 20 }}>
                <table className="DashboardTable" style={{ marginBottom: 15 }}>
                  <thead><tr><th>Section</th><th>Title</th><th>Active</th><th>Sort</th><th>Actions</th></tr></thead>
                  <tbody>
                    {extras.map((ex, idx) => (
                      <tr key={ex.id} draggable onDragStart={(e) => onExtraDragStart(e, idx)} onDragOver={onExtraDragOver} onDrop={(e) => onExtraDrop(e, idx)} onDragEnd={() => setDragIndex(null)} style={{ cursor: 'move', background: dragIndex === idx ? 'rgba(0,0,0,0.05)' : undefined }}>
                        <td>{ex.section_key}</td><td>{ex.title}</td><td>{ex.active ? 'Yes' : 'No'}</td><td>{ex.sort_order}</td>
                        <td>
                          <button type="button" onClick={() => handleExtraEdit(ex)} className="DashboardButtonSecondary" style={{ marginRight: 8 }}>Edit</button>
                          <button type="button" onClick={() => handleExtraToggle(ex)} className="DashboardButtonSecondary">{ex.active ? 'Disable' : 'Enable'}</button>
                        </td>
                      </tr>
                    ))}
                    {extras.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center' }}>No extras yet.</td></tr>}
                  </tbody>
                </table>
                <div ref={extrasFormRef} className="DashboardForm" style={{ padding: 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                    <div className="DashboardFormGroup"><label className="DashboardFormLabel">Section Key</label><input type="text" name="section_key" value={extraForm.section_key} onChange={handleExtraChange} className="DashboardFormInput" /></div>
                    <div className="DashboardFormGroup"><label className="DashboardFormLabel">Title</label><input type="text" name="title" value={extraForm.title} onChange={handleExtraChange} className="DashboardFormInput" /></div>
                  </div>
                  <div className="DashboardFormGroup"><label className="DashboardFormLabel">Description (HTML)</label><textarea name="description" value={extraForm.description} onChange={handleExtraChange} className="DashboardFormInput" rows="4" /></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                    <div className="DashboardFormGroup"><label className="DashboardFormLabel">Sort Order</label><input type="number" name="sort_order" value={extraForm.sort_order} onChange={handleExtraChange} className="DashboardFormInput" /></div>
                    <div className="DashboardFormGroup" style={{ display: 'flex', alignItems: 'center' }}><label className="DashboardFormLabel" style={{ marginRight: 10 }}>Active</label><input type="checkbox" name="active" checked={!!extraForm.active} onChange={handleExtraChange} /></div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    {extraForm.id ? <button type="button" className="DashboardButtonSecondary" onClick={resetExtraForm}>Cancel Edit</button> : null}
                    <button type="button" onClick={handleExtraSubmit} className="DashboardButton">{extraForm.id ? 'Update Extra' : 'Add Extra'}</button>
                  </div>
                </div>
              </div>
            ) : <p>Save the beach first to manage extras.</p>}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" className="DashboardButtonSecondary" onClick={() => setIsEditing(false)}>Cancel</button>
              <button type="submit" className="DashboardButton">{selectedItem ? 'Update Beach' : 'Add Beach'}</button>
            </div>
          </form>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 15 }}>
              <div>
                <label className="DashboardFormLabel" style={{ display: 'block', marginBottom: 4 }}>Filter by Country</label>
                <select name="country_id" value={filters.country_id} onChange={handleFilterChange} className="DashboardFormInput" style={{ minWidth: 220 }}>
                  <option value="">All Countries</option>
                  {countries.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <div>
                <label className="DashboardFormLabel" style={{ display: 'block', marginBottom: 4 }}>Status</label>
                <select name="active" value={filters.active} onChange={handleFilterChange} className="DashboardFormInput" style={{ minWidth: 160 }}>
                  <option value="">All</option><option value="1">Active</option><option value="0">Inactive</option>
                </select>
              </div>
              <div style={{ marginTop: 22, display: 'flex', gap: 10 }}>
                <button className="DashboardButton" onClick={applyFilters} type="button">Apply Filters</button>
                <button className="DashboardButtonSecondary" onClick={resetFilters} type="button">Reset</button>
              </div>
            </div>
        {/* Drag rows to set the order this module's carousel uses on the country page. */}
        <div className={`reorder-hint ${reorder.error ? 'is-error' : reorder.enabled ? 'is-on' : ''}`}>
          {reorder.error
            ? reorder.error
            : reorder.isSaving
              ? 'Saving order...'
              : reorder.enabled
                ? '⠿ Drag a row to move it. This is the order on the country page; it saves straight away.'
                : 'Order on the site: choose a country in the filter above and press Apply Filters. Then drag the rows (⠿) into the order they should appear on that country\u2019s page.'}
        </div>
            <table className="DashboardTable">
              <thead><tr><th>Title</th><th>Country</th><th>Type</th><th>LGBTQ+</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {items.map((item, rowIdx) => (
                  <tr key={item.id} {...reorder.rowProps(rowIdx)}>
                    <td>{item.title}</td><td>{item.country || ''}</td><td>{item.beach_type || '-'}</td>
                    <td>{item.lgbtq_friendly ? 'Yes' : 'No'}</td><td>{item.active ? 'Yes' : 'No'}</td>
                    <td>
                      <button onClick={() => handleEdit(item)} className="DashboardButtonSecondary" style={{ marginRight: '8px' }}><FaEdit /> Edit</button>
                      <button onClick={() => handleDelete(item.id)} className="DashboardButtonSecondary" style={{ color: '#d32f2f' }}><FaTrash /> Delete</button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center' }}>No beaches found.</td></tr>}
              </tbody>
            </table>
          </>
        )}
      </div>

      {cropperFile && (
        <ImageCropper
          file={cropperFile}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          onSkip={handleSkipCrop}
          aspectRatio={16/9}
          aspectRatioLabel="16:9 (Landscape - recommended for galleries)"
          minWidth={800}
        />
      )}
    </div>
  );
}
